import { sportOrganizationRouteNames } from '@/routing/routeNames/sportOrganizerRouteNames'

const {
  sportOrganizationEvents,
  sportLiveEvents,
  sportPastEvents,
  sportOrganizationProfileInfo,
  sportOrganizationFinanceInfo,
  sportOrganizationTicketCategories,
  sportOrganizationSubscriptionsPlan,
  sportOrganizationSubscriptionsList,
  sportOrganizationMemberships,
  sportOrganizationMembersList,
  sportOrganizationSubscribers,
  sportOrganizationMessages,
  sportOrganizationPromoCodes,
  sportOrganizationPromoCodeInfo,
  sportOrganizationReports,
  sportOrganizationAddEvent,
  sportOrganizationMailingList,
  sportOrganizationUsers,
  sportOrganizationSubscriptionsEmails,
  sportOrganizationSubscriptionRequirements,
  sportOrganizationSubscriptionsImport,
  sportOrganizationCreateSubscriptionEmail,
  sportOrganizationMembershipsEmails,
  sportOrganizationCreateMembershipEmail,
  sportOrganizationMembershipRequirements,
  sportOrganizationMembershipsImport,
  sportOrganizationMembersCreate,
  sportOrganizationMembersUpdate,
  sportOrganizationSubscriptionStatistics,
  sportOrganizationMembershipStatistics,
  sportOrganizationSubscriptionUpdate,
  sportOrganizationMembershipsEmailsNew,
} = sportOrganizationRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  events: sportOrganizationEvents,
  liveEvents: sportLiveEvents,
  pastEvents: sportPastEvents,
  organizationInfo: sportOrganizationProfileInfo,
  financeInfo: sportOrganizationFinanceInfo,
  subscribers: sportOrganizationSubscribers,
  subscriptionsPlan: sportOrganizationSubscriptionsPlan,
  subscriptionsList: sportOrganizationSubscriptionsList,
  promoCodes: sportOrganizationPromoCodes,
  memberships: sportOrganizationMemberships,
  membersList: sportOrganizationMembersList,
  reports: sportOrganizationReports,
  ticketCategories: sportOrganizationTicketCategories,
  messages: sportOrganizationMessages,
  subscriptionsEmails: sportOrganizationSubscriptionsEmails,
  subscriptionRequirements: sportOrganizationSubscriptionRequirements,
  membershipsEmails: sportOrganizationMembershipsEmails,
  membershipRequirements: sportOrganizationMembershipRequirements,
  subscriptionStatistics: sportOrganizationSubscriptionStatistics,
  membershipStatistics: sportOrganizationMembershipStatistics,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const sportOrganizationSidebarConfiguration = [
  {
    label: 'My events',
    key: sidebarKeys.events,
    icon: 'events',
    subroutes: [
      {
        label: 'Live events',
        key: sidebarKeys.liveEvents,
        linkedRoutes: [sportLiveEvents],
        internalRoute: {
          name: sportLiveEvents,
        },
      },
      {
        label: 'Past events',
        key: sidebarKeys.pastEvents,
        linkedRoutes: [sportPastEvents],
        internalRoute: {
          name: sportPastEvents,
        },
      },
    ],
    linkedRoutes: [
      sportOrganizationEvents,
      sportOrganizationAddEvent,
      sportLiveEvents,
      sportPastEvents,
    ],
    internalRoute: {
      name: sportLiveEvents,
    },
  },
  {
    label: 'Organization info',
    key: sidebarKeys.organizationInfo,
    icon: 'organization-info',
    linkedRoutes: [sportOrganizationProfileInfo],
    internalRoute: {
      name: sportOrganizationProfileInfo,
    },
  },
  {
    label: 'Finance info',
    key: sidebarKeys.financeInfo,
    linkedRoutes: [sportOrganizationFinanceInfo],
    icon: 'finance-info',
    internalRoute: {
      name: sportOrganizationFinanceInfo,
    },
  },
  {
    label: 'Ticket categories',
    key: sidebarKeys.ticketCategories,
    linkedRoutes: [sportOrganizationTicketCategories],
    icon: 'ticket-types',
    internalRoute: {
      name: sportOrganizationTicketCategories,
    },
  },
  {
    label: 'Mailing list',
    key: sidebarKeys.mailingList,
    icon: 'subscribers',
    linkedRoutes: [
      sportOrganizationSubscribers,
      sportOrganizationMailingList,
      sportOrganizationMessages,
    ],
    internalRoute: {
      name: sportOrganizationSubscribers,
    },
    subroutes: [
      {
        label: 'Subscribers list',
        key: sidebarKeys.subscribers,
        linkedRoutes: [sportOrganizationSubscribers],
        internalRoute: {
          name: sportOrganizationSubscribers,
        },
      },
      {
        label: 'Mobile app messages',
        key: sidebarKeys.messages,
        linkedRoutes: [sportOrganizationMessages],
        internalRoute: {
          name: sportOrganizationMessages,
        },
      },
    ],
  },
  {
    label: 'Users',
    key: sidebarKeys.users,
    icon: 'people',
    linkedRoutes: [sportOrganizationUsers],
    internalRoute: {
      name: sportOrganizationUsers,
    },
  },
  {
    label: 'Subscriptions',
    key: sidebarKeys.subscriptionsList,
    icon: 'subscriptions',
    subroutes: [
      {
        label: 'Subscriptions Plans',
        key: sidebarKeys.subscriptionsPlan,
        linkedRoutes: [sportOrganizationSubscriptionsPlan],
        internalRoute: {
          name: sportOrganizationSubscriptionsPlan,
        },
      },
      {
        label: 'Subscribers List',
        key: sidebarKeys.subscriptionsList,
        linkedRoutes: [sportOrganizationSubscriptionsList, sportOrganizationSubscriptionsImport, sportOrganizationSubscriptionUpdate],
        internalRoute: {
          name: sportOrganizationSubscriptionsList,
        },
      },
      {
        label: 'Emails to Subscribers',
        key: sidebarKeys.subscriptionsEmails,
        linkedRoutes: [
          sportOrganizationSubscriptionsEmails,
          sportOrganizationCreateSubscriptionEmail,
        ],
        internalRoute: {
          name: sportOrganizationSubscriptionsEmails,
        },
      },
      {
        label: 'Subscription Requirements',
        key: sidebarKeys.subscriptionRequirements,
        linkedRoutes: [sportOrganizationSubscriptionRequirements],
        internalRoute: {
          name: sportOrganizationSubscriptionRequirements,
        },
      },
      {
        label: 'Subscription Statistics',
        key: sidebarKeys.subscriptionStatistics,
        linkedRoutes: [sportOrganizationSubscriptionStatistics],
        internalRoute: {
          name: sportOrganizationSubscriptionStatistics,
        },
      },
    ],
    linkedRoutes: [sportOrganizationSubscriptionsPlan, sportOrganizationSubscriptionsList],
    internalRoute: {
      name: sportOrganizationSubscriptionsPlan,
    },
  },
  {
    label: 'Promo codes',
    key: sidebarKeys.promoCodes,
    icon: 'promo-codes',
    linkedRoutes: [sportOrganizationPromoCodes, sportOrganizationPromoCodeInfo],
    internalRoute: {
      name: sportOrganizationPromoCodes,
    },
  },
  {
    label: 'Memberships',
    key: sidebarKeys.memberships,
    icon: 'memberships',
    subroutes: [
      {
        label: 'Memberships',
        key: sidebarKeys.memberships,
        linkedRoutes: [sportOrganizationMemberships],
        internalRoute: {
          name: sportOrganizationMemberships,
        },
      },
      {
        label: 'Members List',
        key: sidebarKeys.membersList,
        linkedRoutes: [
          sportOrganizationMembersList,
          sportOrganizationMembershipsImport,
          sportOrganizationMembersCreate,
          sportOrganizationMembersUpdate,
        ],
        internalRoute: {
          name: sportOrganizationMembersList,
        },
      },
      {
        label: 'Emails to Members',
        key: sidebarKeys.membershipsEmails,
        linkedRoutes: [sportOrganizationMembershipsEmails, sportOrganizationMembershipsEmailsNew],
        internalRoute: {
          name: sportOrganizationMembershipsEmails,
        },
      },
      {
        label: 'Membership Requirements',
        key: sidebarKeys.membershipRequirements,
        linkedRoutes: [sportOrganizationMembershipRequirements],
        internalRoute: {
          name: sportOrganizationMembershipRequirements,
        },
      },
      {
        label: 'Membership Statistics',
        key: sidebarKeys.membershipStatistics,
        linkedRoutes: [sportOrganizationMembershipStatistics],
        internalRoute: {
          name: sportOrganizationMembershipStatistics,
        },
      },
    ],
    linkedRoutes: [sportOrganizationMemberships, sportOrganizationMembersList],
    internalRoute: {
      name: sportOrganizationMembersList,
    },
  },
  {
    label: 'Reports',
    key: sidebarKeys.reports,
    icon: 'reports',
    linkedRoutes: [sportOrganizationReports],
    internalRoute: {
      name: sportOrganizationReports,
    },
  },
]
